// src/pages/Hechos.jsx
// Registro de hechos operativos. La bandeja del módulo, presentada como un
// registro documental y no como una tabla de tickets: cada renglón es un caso
// con nombre propio, no una fila de sistema.
//
// El servidor ya recorta lo que cada perfil puede ver, así que aquí no hay
// lógica de permisos más allá de mostrar u ocultar el botón de levantar.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { listIncidents, bootstrap, exportarReporteHO, exportFiltros, listLawyers, ETAPAS, etapaLabel } from '../services/incidentApi';
import { HechosStyles, Membrete, I, Pill, fmtMonto, diasDesde } from '../components/hechos/ui';

const TONO_ETAPA = {
    recibido: 'neutro', asignado: 'info', revisado: 'neutro',
    documentacion: 'pend', gestion_legal: 'info', concluido: 'ok',
};

/** Tarjeta de acción del escritorio legal: el número ES el filtro. */
function TileAccion({ n, titulo, detalle, tono, activo, onToggle }) {
    const cero = !n;
    return (
        <button type="button" className={`ho-tile ${cero && !activo ? 'cero' : ''}`}
                aria-pressed={activo} disabled={cero && !activo} onClick={onToggle}>
            <div className="n" style={!cero && tono ? { color: `var(--ho-${tono})` } : undefined}>{n}</div>
            <div className="k">{titulo}</div>
            <div className="d">{cero ? 'al corriente' : detalle}</div>
        </button>
    );
}

export default function Hechos() {
    const navigate = useNavigate();

    const [items, setItems]   = useState([]);
    const [total, setTotal]   = useState(0);
    const [boot, setBoot]     = useState(null);
    const [perfil, setPerfil] = useState(null);
    const [acciones, setAcciones] = useState(null);  // conteos "requiere tu atención"
    const [abogadosCat, setAbogadosCat] = useState(null);
    const [cargando, setCargando] = useState(true);

    // Filtros del registro
    const [filtro, setFiltro]     = useState('');    // etapa
    const [fAccion, setFAccion]   = useState('');    // tarjeta de acción activa
    const [q, setQ]               = useState('');    // búsqueda libre (input)
    const [qDeb, setQDeb]         = useState('');    // búsqueda aplicada (debounce)
    const [fEstado, setFEstado]   = useState('');
    const [fTipo, setFTipo]       = useState('');
    const [fAbogado, setFAbogado] = useState('');

    useEffect(() => {
        const t = setTimeout(() => setQDeb(q.trim()), 350);
        return () => clearTimeout(t);
    }, [q]);

    // Arranque: perfil, catálogos, tendencia. Una sola vez.
    useEffect(() => {
        let vivo = true;
        bootstrap()
            .then(b => {
                if (!vivo) return;
                setBoot(b);
                setPerfil(b.perfil);
                if (b.perfil?.canSeeLegal) {
                    // El filtro por abogado solo tiene sentido para quien ve
                    // casos de otros: admin, coordinadores y dirección.
                    if (b.perfil.isAdmin || ['coordinador', 'director'].includes(b.perfil.legalRole)) {
                        listLawyers().then(r => vivo && setAbogadosCat(r.abogados)).catch(() => {});
                    }
                }
            })
            .catch(e => {
                if (e.response?.status === 403) {
                    toast.error('No tienes acceso al módulo de Hechos Operativos');
                    navigate('/dashboard');
                } else {
                    toast.error('No se pudo cargar el módulo');
                }
            });
        return () => { vivo = false; };
    }, [navigate]);

    // La lista reacciona a cada filtro; los conteos de acción llegan con ella.
    useEffect(() => {
        let vivo = true;
        setCargando(true);
        const params = {};
        if (filtro)   params.etapa = filtro;
        if (qDeb)     params.q = qDeb;
        if (fEstado)  params.estado = fEstado;
        if (fTipo)    params.eventType = fTipo;
        if (fAbogado) params.abogado = fAbogado;
        if (fAccion)  params.accion = fAccion;
        listIncidents(params)
            .then(data => {
                if (!vivo) return;
                setItems(data.items || []);
                setTotal(data.total || 0);
                if (data.acciones) setAcciones(data.acciones);
            })
            .catch(e => {
                if (e.response?.status !== 403) toast.error('No se pudo cargar el registro');
            })
            .finally(() => vivo && setCargando(false));
        return () => { vivo = false; };
    }, [filtro, qDeb, fEstado, fTipo, fAbogado, fAccion]);

    const hayFiltros = !!(filtro || q || fEstado || fTipo || fAbogado || fAccion);
    function limpiarFiltros() {
        setFiltro(''); setFAccion(''); setQ('');
        setFEstado(''); setFTipo(''); setFAbogado('');
    }

    // ── Descarga del Reporte HO con sus propios filtros ──────────────────
    const [expAbierto, setExpAbierto] = useState(false);
    const [expCat, setExpCat] = useState(null);       // { marcas, sucursales }
    const [expDesde, setExpDesde] = useState('');
    const [expHasta, setExpHasta] = useState('');
    const [expMarca, setExpMarca] = useState('');
    const [expSucursal, setExpSucursal] = useState('');
    const [descargando, setDescargando] = useState(false);

    async function abrirExport() {
        setExpAbierto(true);
        if (expCat === null) {
            exportFiltros().then(setExpCat).catch(() => setExpCat({ marcas: [], sucursales: [] }));
        }
    }

    async function descargarReporte() {
        setDescargando(true);
        try {
            await exportarReporteHO({
                ...(expDesde ? { desde: expDesde } : {}),
                ...(expHasta ? { hasta: expHasta } : {}),
                ...(expMarca ? { marca: expMarca } : {}),
                ...(expSucursal ? { sucursal: expSucursal } : {}),
            });
            setExpAbierto(false);
        } catch {
            toast.error('No se pudo generar el reporte');
        } finally {
            setDescargando(false);
        }
    }

    // Solo legal/admin ven la analítica y los montos agregados. El gerente que
    // levanta la incidencia ve únicamente sus casos, para dar seguimiento y
    // subir documentos — no la data del negocio (detrimento, mapa de riesgo).
    const veAnalitica = !!perfil?.canSeeLegal;
    const cols4 = { gridTemplateColumns: '130px minmax(0,1fr) 160px 120px' };

    return (
        <div className="ho-wrap">
            <HechosStyles />
            <Membrete />

            <header className="ho-head" style={{ paddingTop: 16 }}>
                <div className="d-flex align-items-end flex-wrap gap-3">
                    <div style={{ flex: 1, minWidth: 240 }}>
                        <div className="ho-kicker">{veAnalitica ? 'Registro' : 'Seguimiento'}</div>
                        <h1 className="ho-h1">{veAnalitica ? 'Hechos operativos' : 'Mis hechos operativos'}</h1>
                        <div className="ho-head-sub">
                            <span style={{ textTransform: 'capitalize' }}>
                                {new Date().toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                            </span>
                            <span>{veAnalitica
                                ? 'Expedientes de tus zonas'
                                : 'Da seguimiento a lo que has levantado y sube la documentación que falte'}</span>
                        </div>
                    </div>
                    <div className="d-flex gap-2 flex-wrap align-items-center">
                        {veAnalitica && (
                            <>
                                <button className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                                        onClick={abrirExport}>
                                    {I.download} Reporte HO
                                </button>
                                <button className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                                        onClick={() => navigate('/hechos/riesgo')}>
                                    {I.chart} Mapa de riesgo
                                </button>
                            </>
                        )}
                        {perfil?.canReport && (
                            <button className="btn btn-primary d-inline-flex align-items-center gap-2"
                                    onClick={() => navigate('/hechos/nuevo')}>
                                {I.plus} Levantar hecho
                            </button>
                        )}
                    </div>
                </div>
            </header>

            {/* ── Requiere tu atención: cada tarjeta filtra la lista ──── */}
            {veAnalitica && acciones && (
                <div className="ho-tiles">
                    <TileAccion n={acciones.porValidar} titulo="Relatos por validar" tono="pend"
                                detalle="sin versión oficial todavía"
                                activo={fAccion === 'por_validar'}
                                onToggle={() => setFAccion(a => a === 'por_validar' ? '' : 'por_validar')} />
                    <TileAccion n={acciones.sinMovimiento} titulo="Sin movimiento" tono="pend"
                                detalle="2 días o más sin actividad"
                                activo={fAccion === 'sin_movimiento'}
                                onToggle={() => setFAccion(a => a === 'sin_movimiento' ? '' : 'sin_movimiento')} />
                    <TileAccion n={acciones.sinAsignar} titulo="Sin abogado" tono="hecho"
                                detalle="esperan asignación"
                                activo={fAccion === 'sin_asignar'}
                                onToggle={() => setFAccion(a => a === 'sin_asignar' ? '' : 'sin_asignar')} />
                </div>
            )}

            {/* ── Búsqueda y filtros: barra de herramientas del registro ── */}
            <div className="ho-filterbar">
                <div className="ho-search">
                    <span className="ic">{I.search}</span>
                    <input className="form-control"
                           placeholder="Buscar folio, colaborador, reportante o municipio…"
                           value={q} onChange={e => setQ(e.target.value)} />
                </div>
                <select className={`form-select${filtro ? ' activo' : ''}`}
                        value={filtro} onChange={e => setFiltro(e.target.value)}>
                    <option value="">Todas las etapas</option>
                    {ETAPAS.map(e => <option key={e.key} value={e.key}>{e.label}</option>)}
                </select>
                {veAnalitica && (
                    <>
                        <select className={`form-select${fEstado ? ' activo' : ''}`}
                                value={fEstado} onChange={e => setFEstado(e.target.value)}>
                            <option value="">Todos los estados</option>
                            {(boot?.estadosCatalogo || []).map(e => <option key={e} value={e}>{e}</option>)}
                        </select>
                        <select className={`form-select${fTipo ? ' activo' : ''}`}
                                value={fTipo} onChange={e => setFTipo(e.target.value)}>
                            <option value="">Todos los tipos</option>
                            {(boot?.tiposEvento || []).map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                        </select>
                        {abogadosCat?.length > 0 && (
                            <select className={`form-select${fAbogado ? ' activo' : ''}`}
                                    value={fAbogado} onChange={e => setFAbogado(e.target.value)}>
                                <option value="">Todos los abogados</option>
                                {abogadosCat.map(a => <option key={a._id} value={a._id}>{a.name}</option>)}
                            </select>
                        )}
                    </>
                )}
                {hayFiltros && (
                    <button className="ho-chip" onClick={limpiarFiltros}>× Limpiar filtros</button>
                )}
            </div>

            {cargando ? (
                // Skeleton: la pantalla llega dibujada mientras el registro carga
                <div style={{ marginTop: 22 }}>
                    {[0, 1, 2, 3, 4].map(i => (
                        <div key={i} className="d-flex align-items-center gap-3"
                             style={{ padding: '17px 0', borderBottom: '1px solid var(--c-border)' }}>
                            <span className="ho-skel" style={{ width: 92, height: 18 }} />
                            <span className="ho-skel" style={{ flex: 1, maxWidth: 380, height: 15 }} />
                            <span className="ho-skel" style={{ width: 130, height: 14 }} />
                            <span className="ho-skel" style={{ width: 84, height: 14 }} />
                        </div>
                    ))}
                </div>
            ) : !items.length ? (
                <div className="ho-empty">
                    <div>{I.shield}</div>
                    <div className="t">
                        {filtro
                            ? <>Nada en “{etapaLabel(filtro)}”</>
                            : hayFiltros
                                ? 'Sin resultados con estos filtros'
                                : 'Sin hechos operativos registrados'}
                    </div>
                    <div style={{ fontSize: 13, maxWidth: 420, margin: '0 auto' }}>
                        {hayFiltros
                            ? 'Ajusta la búsqueda o límpialos para ver todo el registro.'
                            : veAnalitica
                                ? 'Cuando un gerente levante un hecho de tus zonas, aparecerá aquí con su folio y su etapa.'
                                : 'Cuando levantes un hecho, aquí verás su folio, en qué etapa va y lo que falte de tu parte.'}
                    </div>
                    {perfil?.canReport && !hayFiltros && (
                        <button className="btn btn-primary d-inline-flex align-items-center gap-2 mt-3"
                                onClick={() => navigate('/hechos/nuevo')}>
                            {I.plus} Levantar el primer hecho
                        </button>
                    )}
                </div>
            ) : (
                <>
                    <div className="d-flex align-items-baseline gap-2" style={{ margin: '18px 0 4px' }}>
                        <span className="ho-kicker">Expedientes</span>
                        <span style={{ fontSize: 12, color: 'var(--c-text-3)' }}>
                            {total} en {filtro ? `"${etapaLabel(filtro)}"` : 'el registro'}
                            {items.length < total ? ` · mostrando los ${items.length} más recientes` : ''}
                        </span>
                    </div>
                    <div className="ho-reg-head" style={veAnalitica ? undefined : cols4}>
                        <span>Folio</span><span>Hecho</span><span>Etapa</span><span>{veAnalitica ? 'Zona' : 'Siguiente paso'}</span>
                        {veAnalitica && <span className="am">Detrimento</span>}
                    </div>
                    <div className="ho-reg">
                        {items.map(i => (
                            <button key={i._id} className="ho-reg-row" style={veAnalitica ? undefined : cols4}
                                    onClick={() => navigate(`/hechos/${i._id}`)}>
                                <span><span className="ho-folio">{i.folio}</span></span>
                                <span>
                                    <span className="ti">
                                        {i.eventType?.name || 'Sin clasificar'}
                                        {i.colaboradores?.[0]?.nombre ? ` — ${i.colaboradores[0].nombre}` : ''}
                                    </span>
                                    <span className="su">
                                        {[i.marca, i.municipio].filter(Boolean).join(' · ')}
                                        {i.createdAt ? ` · hace ${diasDesde(i.createdAt)} d` : ''}
                                        {/* Señales de trabajo para el abogado: qué expedientes
                                            piden SU acción, sin abrirlos uno por uno. */}
                                        {veAnalitica && !i.relatoValidado && i.etapa !== 'concluido' &&
                                            <span style={{ color: 'var(--ho-pend)', fontWeight: 600 }}> · relato por validar</span>}
                                        {veAnalitica && i.pendientes?.legal > 0 &&
                                            <span style={{ color: 'var(--ho-pend)', fontWeight: 600 }}> · {i.pendientes.legal} de legal</span>}
                                    </span>
                                </span>
                                <span>
                                    <Pill tone={TONO_ETAPA[i.etapa] || 'neutro'}>{etapaLabel(i.etapa)}</Pill>
                                    {/* Avance de un vistazo, sin abrir el expediente */}
                                    <span className="ho-minitrack">
                                        {ETAPAS.map((e, idx) => {
                                            const actual = ETAPAS.findIndex(x => x.key === i.etapa);
                                            return <i key={e.key} className={idx < actual ? 'done' : idx === actual ? 'now' : ''} />;
                                        })}
                                    </span>
                                </span>
                                {veAnalitica ? (
                                    <span style={{ fontSize: 12.5, color: 'var(--c-text-2)' }}>
                                        {i.estado || '—'}
                                        {i.assignedLawyer?.name && (
                                            <span style={{ display: 'block', fontSize: 11.5, color: 'var(--c-text-3)' }}>
                                                {i.assignedLawyer.name}
                                            </span>
                                        )}
                                    </span>
                                ) : (
                                    // Al gerente la zona legal no le dice nada; lo que
                                    // necesita saber es si la pelota está en su cancha.
                                    <span>
                                        {i.pendientes?.gerente > 0
                                            ? <Pill tone="pend">Te faltan {i.pendientes.gerente} {i.pendientes.gerente === 1 ? 'documento' : 'documentos'}</Pill>
                                            : i.etapa === 'concluido'
                                                ? <Pill tone="ok">Concluido</Pill>
                                                : <Pill tone="neutro">En manos de legal</Pill>}
                                    </span>
                                )}
                                {veAnalitica && <span className="am">{fmtMonto(i.montoDetrimento)}</span>}
                            </button>
                        ))}
                    </div>
                </>
            )}

            {/* ── Descargar Reporte HO: fechas, unidad de negocio y sucursal ── */}
            {expAbierto && (
                <div className="ho-modal-bk" onClick={e => e.target === e.currentTarget && setExpAbierto(false)}>
                    <div className="ho-modal" role="dialog" aria-modal="true" aria-label="Descargar Reporte HO">
                        <div className="ho-kicker">Reportería</div>
                        <h2 className="ho-h2" style={{ marginTop: 6 }}>Descargar Reporte HO</h2>
                        <div className="row g-3" style={{ marginTop: 4 }}>
                            <div className="col-6">
                                <label className="ho-lbl">Desde</label>
                                <input type="date" className="form-control" value={expDesde}
                                       onChange={e => setExpDesde(e.target.value)} />
                            </div>
                            <div className="col-6">
                                <label className="ho-lbl">Hasta</label>
                                <input type="date" className="form-control" value={expHasta}
                                       onChange={e => setExpHasta(e.target.value)} />
                            </div>
                            <div className="col-6">
                                <label className="ho-lbl">Unidad de negocio</label>
                                <select className="form-select" value={expMarca}
                                        onChange={e => setExpMarca(e.target.value)}>
                                    <option value="">Todas</option>
                                    {(expCat?.marcas || []).map(m => <option key={m} value={m}>{m}</option>)}
                                </select>
                            </div>
                            <div className="col-6">
                                <label className="ho-lbl">Sucursal</label>
                                <select className="form-select" value={expSucursal}
                                        onChange={e => setExpSucursal(e.target.value)}>
                                    <option value="">Todas</option>
                                    {(expCat?.sucursales || []).map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        </div>
                        <p className="aviso">
                            Excel con las 17 columnas del formato de legal, un renglón por expediente de tu alcance.
                            Sin fechas, incluye los últimos 12 meses.
                        </p>
                        <div className="d-flex justify-content-end gap-2 mt-3">
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => setExpAbierto(false)}>Cancelar</button>
                            <button className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2"
                                    onClick={descargarReporte} disabled={descargando}>
                                {I.download} {descargando ? 'Generando…' : 'Descargar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
